import { useWeb3State } from "@/web3/context";
import useERC20Contract from "@/web3/contracts/common/contracts/ERC20/useERC20Contract";
import { useQuery } from "@tanstack/react-query";
import { formatUnits } from "ethers";
import { NativeCurrencyIdPerChain } from "../constants";
import { formatDecimals } from "../currencies";
import useCryptoInUSD from "./useCryptoInUSD";
import usePaymentCurrency from "./usePaymentCurrency";

export default function useAccountBalance(
  paymentErc20TokenAddress?: string,
  address?: string | undefined,
) {
  const { web3, currentAddress, network } = useWeb3State();

  const paymentMethod = usePaymentCurrency(paymentErc20TokenAddress);
  const ERC20 = useERC20Contract(paymentErc20TokenAddress);

  const targetAddress = address || currentAddress;
  const getUserBalance = async () => {
    if (!web3 || !targetAddress || !ERC20)
      return {
        rounded: 0,
        raw: 0,
      };

    let balance: bigint;
    const nativeCurrencyId = NativeCurrencyIdPerChain[network];
    if (paymentMethod!.id === nativeCurrencyId) {
      balance = await web3.getBalance(targetAddress);
    } else {
      balance = await ERC20!.balanceOf(targetAddress);
    }

    const balanceInHighDenom = formatUnits(balance, paymentMethod?.decimals);

    return {
      rounded: formatDecimals(balanceInHighDenom),
      raw: balance,
    };
  };

  const { data: balanceInCrypto } = useQuery({
    queryKey: ["account-balance", targetAddress, paymentErc20TokenAddress],
    queryFn: getUserBalance,
    enabled: !!(web3 && targetAddress && paymentMethod),
    staleTime: 0,
  });

  const { cryptoInUSD } = useCryptoInUSD(balanceInCrypto?.rounded);

  const roundedBalance = balanceInCrypto?.rounded;

  return {
    balanceInCrypto:
      roundedBalance || roundedBalance === 0 ? roundedBalance : "-",
    balanceInRaw: balanceInCrypto?.raw,
    balanceInUSD: cryptoInUSD,
  };
}
